import { useSyncExternalStore } from 'react'

export type TutorialVideoPreloadSnapshot = {
    started: boolean
    ready: boolean
    loaded: number
    total: number
}

const tutorialVideoElements = new Map<string, HTMLVideoElement>()
const tutorialVideoListeners = new Set<() => void>()
let tutorialVideoSnapshot: TutorialVideoPreloadSnapshot = {
    started: false,
    ready: false,
    loaded: 0,
    total: 0,
}

function emitTutorialVideoPreloadChange() {
    for (const listener of tutorialVideoListeners) listener()
}

function getTutorialVideoPreloadSnapshot() {
    return tutorialVideoSnapshot
}

function subscribeToTutorialVideoPreload(listener: () => void) {
    tutorialVideoListeners.add(listener)
    return () => tutorialVideoListeners.delete(listener)
}

export function getTutorialVideoSources(isStele: boolean): string[] {
    const suffix = isStele ? '_Stele' : ''
    return [1, 2, 3, 4].map((index) => `/videos/Tutorial-0${index}${suffix}.mp4`)
}

export function preloadTutorialVideos(isStele: boolean): void {
    const sources = getTutorialVideoSources(isStele)
    const loadedSources = sources.filter((src) => tutorialVideoElements.has(src)).length
    tutorialVideoSnapshot = {
        started: true,
        ready: loadedSources === sources.length,
        loaded: loadedSources,
        total: sources.length,
    }
    emitTutorialVideoPreloadChange()

    for (const src of sources) {
        if (tutorialVideoElements.has(src)) continue

        const video = document.createElement('video')
        video.preload = 'auto'
        video.muted = true
        video.playsInline = true
        const markLoaded = () => {
            if (tutorialVideoElements.has(src)) return
            tutorialVideoElements.set(src, video)
            const loaded = tutorialVideoSnapshot.loaded + 1
            tutorialVideoSnapshot = {
                ...tutorialVideoSnapshot,
                ready: loaded >= tutorialVideoSnapshot.total,
                loaded,
            }
            emitTutorialVideoPreloadChange()
        }
        video.addEventListener('loadeddata', markLoaded, { once: true })
        video.addEventListener('error', markLoaded, { once: true })
        video.src = src
        video.load()
    }
}

export function useTutorialVideoPreloadStatus(): TutorialVideoPreloadSnapshot {
    return useSyncExternalStore(
        subscribeToTutorialVideoPreload,
        getTutorialVideoPreloadSnapshot,
        getTutorialVideoPreloadSnapshot,
    )
}
